import type { ReactNode } from 'react';
import {
  CheckCheck,
  CheckCircle2,
  CircleDot,
  FilePlus2,
  Flag,
  Lock,
  MapPin,
  PackageMinus,
  PackagePlus,
  Route,
  Truck,
  UserRound,
  Users,
  XCircle,
} from 'lucide-react';
import {
  formatFieldTime,
  sortTimelineEvents,
  timelineActorBadge,
  timelineProgress,
} from '../../../core/timeline-engine';
import {
  TIMELINE_EVENT_ICONS,
  TIMELINE_EVENT_LABELS,
  TIMELINE_EVENT_TONES,
} from '../../../types/timeline';
import type { TimelineEvent, TimelineOrder } from '../../../types/timeline';

/**
 * Icon key → glyph. The keys live in `src/types/timeline.ts`, which the store
 * and the healthcheck both import and neither may pull React into; this map is
 * the one place they become something you can look at.
 */
const ICONS: Record<string, ReactNode> = {
  'file-plus': <FilePlus2 size={15} aria-hidden />,
  users: <Users size={15} aria-hidden />,
  'check-circle': <CheckCircle2 size={15} aria-hidden />,
  truck: <Truck size={15} aria-hidden />,
  'map-pin': <MapPin size={15} aria-hidden />,
  'package-plus': <PackagePlus size={15} aria-hidden />,
  route: <Route size={15} aria-hidden />,
  flag: <Flag size={15} aria-hidden />,
  'package-minus': <PackageMinus size={15} aria-hidden />,
  'check-check': <CheckCheck size={15} aria-hidden />,
  lock: <Lock size={15} aria-hidden />,
  'x-circle': <XCircle size={15} aria-hidden />,
};

interface TaskTimelineProps {
  events: readonly TimelineEvent[];
  /** Chronological for the field, newest-first for the manager's log. */
  order?: TimelineOrder;
  /** The progress bar above the feed — off for a compact log. */
  showProgress?: boolean;
  emptyLabel?: string;
  /** Fixed evaluation moment, so "היום" never depends on a re-render. */
  now?: number;
}

/**
 * The task's operational feed: one node per reported step, top to bottom.
 *
 * Every node answers the four questions a field screen is read for — what
 * happened, when, who reported it and where — and nothing here decides any of
 * them: the order, the timestamp wording, the actor badge and the progress are
 * all `src/core/timeline-engine.ts`'s answers. The component only draws them.
 */
export function TaskTimeline({
  events,
  order = 'asc',
  showProgress = true,
  emptyLabel = 'טרם דווחו שלבים במשימה זו.',
  now = Date.now(),
}: TaskTimelineProps) {
  const ordered = sortTimelineEvents(events, order);
  const progress = timelineProgress(events);

  if (ordered.length === 0) {
    return <p className="text-sm text-ink/40">{emptyLabel}</p>;
  }

  return (
    <div className="space-y-3">
      {showProgress ? (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-1.5 font-semibold text-ink/70">
              <CircleDot size={14} className="text-brand-500" aria-hidden />
              {progress.step ? TIMELINE_EVENT_LABELS[progress.step] : 'טרם דווח שלב'}
            </span>
            <span data-dir="ltr" className="tabular-nums text-xs text-ink/50">
              {progress.percent}%
            </span>
          </div>
          <div
            className="h-2 w-full rounded-full bg-canvas border border-line overflow-hidden"
            role="progressbar"
            aria-valuenow={progress.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="התקדמות המשימה"
          >
            <div
              className={[
                'h-full rounded-full transition-[width]',
                progress.isClosed ? 'bg-emerald-500' : 'bg-brand-500',
              ].join(' ')}
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        </div>
      ) : null}

      <ol className="space-y-0">
        {ordered.map((event, index) => {
          const isLast = index === ordered.length - 1;
          return (
            <li key={event.id} className="flex gap-3">
              {/* The rail: the node itself, and the line down to the next one. */}
              <div className="flex flex-col items-center shrink-0">
                <span
                  className={[
                    'w-9 h-9 rounded-full border-2 flex items-center justify-center',
                    TIMELINE_EVENT_TONES[event.eventType],
                  ].join(' ')}
                >
                  {ICONS[TIMELINE_EVENT_ICONS[event.eventType]] ?? <CircleDot size={15} aria-hidden />}
                </span>
                {!isLast ? <span className="w-px grow min-h-4 bg-line" aria-hidden /> : null}
              </div>

              <div className="min-w-0 grow text-right pb-4">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <p className="font-semibold text-sm">{event.statusTitle}</p>
                  <span data-dir="ltr" className="text-xs text-ink/50 tabular-nums">
                    {formatFieldTime(event.timestamp, now)}
                  </span>
                </div>

                <p className="mt-1 inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-lg border border-line bg-canvas text-ink/60">
                  <UserRound size={12} aria-hidden />
                  {timelineActorBadge(event)}
                </p>

                {event.description ? (
                  <p className="mt-1.5 text-sm text-ink/70 leading-relaxed break-words">
                    {event.description}
                  </p>
                ) : null}

                {event.locationText ? (
                  <p className="mt-1 text-xs text-ink/50 flex items-start gap-1.5">
                    <MapPin size={12} className="mt-0.5 shrink-0" aria-hidden />
                    <span className="break-words">{event.locationText}</span>
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
